/**
 * Dynamically loads pay.google.com/gp/p/js/pay.js. Idempotent — if the script
 * is already loaded or loading, reuses the promise.
 */
import {useEffect, useState} from 'react';
import {Platform} from 'react-native';

import {logger} from '../../../../../utils/logger';

const PAY_JS_SRC = 'https://pay.google.com/gp/p/js/pay.js';
let loadPromise: Promise<void> | null = null;

function loadPayJs(): Promise<void> {
  if (loadPromise) return loadPromise;
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Pay SDK requires window'));
  }
  if (window.google?.payments?.api) {
    loadPromise = Promise.resolve();
    return loadPromise;
  }
  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${PAY_JS_SRC}"]`);
    const script = existing ?? document.createElement('script');
    if (!existing) {
      script.src = PAY_JS_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      loadPromise = null;
      reject(new Error('Failed to load Google Pay SDK'));
    });
  });
  return loadPromise;
}

/**
 * @param skip When true, pay.js is NOT injected and the hook is a no-op
 *   (`isLoaded` stays false). Used to avoid loading the SDK on shops that do
 *   not offer Google Pay. Defaults to false so existing callers are unchanged.
 */
export function useGooglePaySDK(skip = false): {isLoaded: boolean; error: string | null} {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (skip || Platform.OS !== 'web') return;

    let cancelled = false;
    loadPayJs()
      .then(() => {
        if (!cancelled) setIsLoaded(true);
      })
      .catch(err => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : String(err);
          logger.error('useGooglePaySDK', 'Failed to load pay.js', {error: msg});
          setError(msg);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [skip]);

  return {isLoaded, error};
}
